import { gsap } from "gsap";
import NavHeight from "../utils/nav-height";

export default class Nav {
    constructor() {
        this.navHeightInstance = new NavHeight();
        this.activeItem = null;
        this.nav = document.querySelector('.nav-menu');
        this.darkSections = document.querySelectorAll('[data-dark-bg]');
        this.orangeSections = document.querySelectorAll('[data-orange-bg]');
        this._lastScrollY = 0;
        this._navIsHidden = false;

        this.init();
    }

    init() {
        this.initHoverSubMenus();
        this.handleOnDark();
        window.addEventListener('resize', () => this.handleOnDark());

        // Mouse offset top pour révéler la nav
        window.addEventListener('mousemove', (e) => {
            if (e.clientY <= 30) {
                this.showNav();
                if (window.scrollY > 0) {
                    this.nav.classList.add('has-background');
                } else {
                    this.nav.classList.remove('has-background');
                }
            }
        });
    }

    // Méthode appelée par Lenis à chaque scroll
    onScroll({ scroll }) {
        this.handleNavAutoHide(scroll);
        this.handleOnDark(scroll);
    }

    handleNavAutoHide(scrollY) {
        const threshold = 5;
        const navResponsive = document.querySelector('.nav-responsive');

        if (scrollY < 20) {
            this.showNav();
            this.nav.classList.remove('has-background');
            navResponsive?.classList.remove('has-background');
        } else if (scrollY < this._lastScrollY - threshold) {
            this.showNav();
            this.nav.classList.add('has-background');
            navResponsive?.classList.add('has-background');
        } else if (scrollY > this._lastScrollY + threshold) {
            this.hideNav();
            navResponsive?.classList.remove('has-background');
        }

        this._lastScrollY = scrollY;
    }

    showNav() {
        if (this._navIsHidden) {
            this.nav.style.transform = "translateY(0)";
            this._navIsHidden = false;
        }
    }

    hideNav() {
        if (!this._navIsHidden) {
            this.nav.style.transform = `translateY(-${this.nav.offsetHeight}px)`;
            this.nav.style.transition = "transform 0.35s cubic-bezier(.83,.06,.34,1)";
            this._navIsHidden = true;
            this.nav.classList.remove('has-background');
        }
    }

    handleOnDark(scrollY = window.scrollY) {
        if (!this.nav || !this.darkSections.length) return;

        const navResponsive = document.querySelector('.nav-responsive');

        const navRect = this.nav.getBoundingClientRect();
        const navY = navRect.top + navRect.height / 2 + scrollY;

        let isOnDark = false;
        let isOnOrange = false;

        this.darkSections.forEach(section => {
            const rect = section.getBoundingClientRect();
            const top = rect.top + scrollY;
            const bottom = rect.bottom + scrollY;
            if (navY >= top && navY <= bottom) {
                isOnDark = true;
            }
        });

        this.orangeSections.forEach(section => {
            const rect = section.getBoundingClientRect();
            const top = rect.top + scrollY;
            const bottom = rect.bottom + scrollY;
            if (navY >= top && navY <= bottom) {
                isOnOrange = true;
            }
        });

        this.nav.classList.toggle('on-dark', isOnDark);
        this.nav.classList.toggle('on-orange', isOnOrange);

        if (navResponsive) {
            navResponsive.classList.toggle('on-dark', isOnDark);
            navResponsive.classList.toggle('on-orange', isOnOrange);
        }
    }

    // === HOVER SUB-MENU ===
    initHoverSubMenus() {
        const menuItems = document.querySelectorAll(".nav-menu .menu_item-has-children");
        const timeouts = new Map();

        const measureSubMenuHeight = (menu) => {
            const originalStyles = {
                visibility: menu.style.visibility,
                position: menu.style.position,
                display: menu.style.display
            };

            menu.style.visibility = "hidden";
            menu.style.position = "absolute";
            menu.style.display = "block";

            const menuHeight = menu.scrollHeight;

            menu.style.visibility = originalStyles.visibility;
            menu.style.position = originalStyles.position;
            menu.style.display = originalStyles.display;

            let maxChildHeight = menuHeight;
            menu.querySelectorAll(".sub-menu").forEach(subMenu => {
                const subMenuHeight = measureSubMenuHeight(subMenu);
                if (subMenuHeight > maxChildHeight) {
                    maxChildHeight = subMenuHeight;
                }
            });

            return maxChildHeight;
        };

        document.querySelectorAll(".nav-menu .megamenu").forEach(megamenu => {
            let maxSubMenuHeight = 0;

            megamenu.querySelectorAll(".sub-menu").forEach(subMenu => {
                const subMenuHeight = measureSubMenuHeight(subMenu);
                if (subMenuHeight > maxSubMenuHeight) {
                    maxSubMenuHeight = subMenuHeight;
                }
            });

            megamenu.style.setProperty("--max-submenu-height", `${maxSubMenuHeight}px`);
        });

        const clearAllTimeouts = () => {
            timeouts.forEach((timeout, key) => {
                clearTimeout(timeout);
                timeouts.delete(key);
            });
        };

        menuItems.forEach((item) => {
            const megamenu = item.querySelector(":scope > .megamenu");
            const subMenus = item.querySelectorAll(":scope > .sub-menu");
            const menuId = `menu-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const handleMouseEnter = () => {
                clearAllTimeouts();

                if (this.activeItem && this.activeItem !== item) {
                    this.activeItem.classList.remove("hover-active");
                    const previousMegamenu = this.activeItem.querySelector(":scope > .megamenu");

                    if (previousMegamenu) {
                        gsap.killTweensOf(previousMegamenu);
                        gsap.set(previousMegamenu, { opacity: 0, visibility: 'hidden', y: -this.navHeightInstance.navHeightValue / 2 });
                    }
                }

                this.activeItem = item;

                if (!item.classList.contains("hover-active")) {
                    item.classList.add("hover-active");

                    const offsetY = this.navHeightInstance.navHeightValue / 2;

                    if (megamenu) {
                        gsap.killTweensOf(megamenu);
                        gsap.fromTo(megamenu,
                            { opacity: 0, visibility: 'hidden', y: -offsetY },
                            { opacity: 1, visibility: 'visible', y: 0, duration: 0.35, ease: "power2.out", overwrite: "auto" }
                        );
                    }
                }
            };

            const handleMouseLeave = () => {
                const timeout = setTimeout(() => {
                    item.classList.remove("hover-active");
                    this.activeItem = null;

                    if (megamenu) {
                        const offsetY = this.navHeightInstance.navHeightValue / 2;
                        gsap.killTweensOf(megamenu);
                        gsap.to(megamenu,
                            { opacity: 0, visibility: 'hidden', y: -offsetY, duration: 0.35, overwrite: "auto" }
                        );
                    }

                    timeouts.delete(menuId);
                }, 200);

                timeouts.set(menuId, timeout);
            };

            item.addEventListener("mouseenter", handleMouseEnter);
            item.addEventListener("mouseleave", handleMouseLeave);

            if (megamenu) {
                megamenu.addEventListener("mouseenter", handleMouseEnter);
                megamenu.addEventListener("mouseleave", handleMouseLeave);
            }

            subMenus.forEach((subMenu) => {
                subMenu.addEventListener("mouseenter", handleMouseEnter);
                subMenu.addEventListener("mouseleave", handleMouseLeave);
            });
        });
    }
}
